<template>
  <v-card 
    class="quiz-card" 
    :class="{ 'quiz-active': isQuizActive, 'quiz-promo': isQuizPromotional, 'quiz-closed': isQuizClosed, 'clickable': isQuizActive || isQuizClosed }"
    elevation="0"
    @click="handleCardClick"
  >
    <!-- Before Quiz Starts - Promotional State -->
    <div v-if="isQuizPromotional" class="promo-state">
      <h2 class="quiz-title">{{ advertisingTitle }}</h2>
      <p class="quiz-description">{{ advertisingDescription }}</p>

      <div class="rules-link" @click.stop="$emit('rules-click')">
        <span>Rules & how to play</span>
        <img :src="require('@/assets/arrow-right.png')" class="rightIcon" />
      </div>
      
      <div class="day-indicators">
        <div 
          v-for="day in totalDays" 
          :key="day"
          class="day-box">
          {{ day }}
        </div>
      </div>
    </div>

    <!-- During Quiz - Active State -->
    <div v-else-if="isQuizActive" class="active-state">
      <div class="quiz-header">
        <img v-if="quiz.thumbUrl && activeQuestionAnswered" :src="quiz.thumbUrl" class="quiz-thumb-icon" />
        <div class="quiz-info">
          <p class="quiz-active-title">{{ activeTitle }}</p>
          <p class="quiz-active-description">{{ activeDescription }}</p>
        </div>
        <div class="entries-section" @click.stop="answers && answers.length > 0 ? $emit('show-stats') : null">
          <div class="entries-label">Entries</div>
          <div class="entries-count">{{ entries }}</div>
        </div>
      </div>
      
      <div class="day-indicators-small" v-if="!activeQuestionAnswered">
        <div 
          v-for="question in allQuestions" 
          :key="question.id"
          class="day-box-small"
          :class="{ 
            'completed': isQuestionAnswered(question),
            'current': question.questionNumber === currentDay,
            'locked': !isQuestionAnswered(question) && question.questionNumber !== currentDay
          }"
          @click.stop="question.questionNumber === currentDay ? $emit('answer-click') : null"
        >
          <template v-if="isQuestionAnswered(question)">
            <img :src="question.thumbUrl" class="thumb-icon" />
          </template>
          <template v-else>
            <img v-if="question.questionNumber === currentDay" :src="require('@/assets/quiz/peel-large.svg')" class="peel-corner" />
            <span class="day-number">{{ question.questionNumber }}</span>
          </template>
        </div>
      </div>

      <div class="rules-link" @click.stop="$emit('answer-click')" v-if="!activeQuestionAnswered">
        <span>Answer today's question</span>
        <img :src="require('@/assets/arrow-right.png')" class="rightIcon" />
      </div>
    </div>

    <!-- After Quiz Ends - Closed State -->
    <div v-else-if="isQuizClosed" class="closed-state">
      <div class="quiz-header">
        <img v-if="quiz.thumbUrl" :src="quiz.thumbUrl" class="quiz-thumb-icon" />
        <div class="quiz-info">
          <p class="quiz-active-title">{{ activeTitle }}</p>
          <p class="quiz-active-description">{{ closedDescription }}</p>
        </div>
        <div class="entries-section" @click.stop="answers && answers.length > 0 ? $emit('show-stats') : null">
          <div class="entries-label">Entries</div>
          <div class="entries-count">{{ entries }}</div>
        </div>
      </div>
    </div>
  </v-card>
</template>

<script>
export default {
  name: 'QuizCard',
  emits: ['rules-click', 'answer-click', 'show-stats'],
  props: {
    quiz: {
      type: Object,
      required: false
    },
    quizPoints: {
      type: Object,
      required: false
    },
    activeQuestionAnswered: {
        type: Boolean,
        default: false
    },
    answers: {
        type: Array,
        default: () => []
    }
  },
  computed: {
    isQuizPromotional() {
      // Show promotional state when between advertiseDateTime and startDateTime
      if (!this.quiz.advertiseDateTime || !this.quiz.startDateTime) {
        return false;
      }
      
      const now = new Date();
      const advertiseDate = new Date(this.quiz.advertiseDateTime);
      const startDate = new Date(this.quiz.startDateTime);
      
      return now >= advertiseDate && now < startDate;
    },
    isQuizActive() {
      // Quiz is active during the quiz period (between startDateTime and endDateTime)
      // BUT not if the final question has been answered
      if (!this.quiz.startDateTime || !this.quiz.endDateTime) {
        return false;
      }
      
      // Check if the final question has been answered
      if (this.quiz.questions && this.quiz.questions.length > 0) {
        const finalQuestion = this.quiz.questions[this.quiz.questions.length - 1];
        const isFinalQuestionAnswered = this.isQuestionAnswered(finalQuestion);
        if (isFinalQuestionAnswered) {
          return false; // Not active if all questions completed
        }
      }
      
      const now = new Date();
      const startDate = new Date(this.quiz.startDateTime);
      const endDate = new Date(this.quiz.endDateTime);
      
      return now >= startDate && now <= endDate;
    },
    isQuizClosed() {
      // Quiz is closed after endDateTime but before showClosedUntilDateTime
      // OR if all questions (including the final one) have been answered
      if (!this.quiz.endDateTime) {
        return false;
      }
      
      const now = new Date();
      const endDate = new Date(this.quiz.endDateTime);
      
      // Check if the final question has been answered
      if (this.quiz.questions && this.quiz.questions.length > 0) {
        const finalQuestion = this.quiz.questions[this.quiz.questions.length - 1];
        const isFinalQuestionAnswered = this.isQuestionAnswered(finalQuestion);
        if (isFinalQuestionAnswered) {
          return true;
        }
      }
      
      // Check if quiz has ended
      if (now <= endDate) {
        return false;
      }
      
      // Check if we're still within the closed display period
      if (this.quiz.showClosedUntilDateTime) {
        const showClosedUntil = new Date(this.quiz.showClosedUntilDateTime);
        return now <= showClosedUntil;
      }
      
      // No showClosedUntilDateTime means show closed indefinitely
      return true;
    },
    advertisingTitle() {
      return this.quiz.advertisingHeader;
    },
    advertisingDescription() {
      return this.quiz.advertisingDescription;
    },
    totalDays() {
      return this.quiz.questions.length;
    },
    activeTitle() {
      return this.quiz.tagLineHeader;
    },
    activeDescription() {
      // Check if today's question was answered
      if (this.activeQuestionAnswered) {
          return "Play again tomorrow to keep up your streak!";
      }
      
      // Default: show today's daily description
      const todaysQuestion = this.quiz.questions.find(q => this.isQuestionToday(q));
      return todaysQuestion ? todaysQuestion.dailyDescription : '';
    },
    closedDescription() {
      // Show custom closed description if available, otherwise default
      return this.quiz.closedDescription || "Now closed - we'll be contacting winners soon!";
    },
    entries() {
        return this.quizPoints.total;
    },
    currentDay() {
        const todaysQuestion = this.quiz.questions.find(q => this.isQuestionToday(q));
        if (todaysQuestion) {
            return this.quiz.questions.indexOf(todaysQuestion) + 1;
        }
        return 0;
    },
    allQuestions() {
        return this.quiz.questions;
    }
  },
  methods: {
    isQuestionAnswered(question) {
      // Check if this question has been answered based on the answers prop
      if (!this.answers || this.answers.length === 0) {
        return false;
      }
      return this.answers.some(answer => answer.question.id === question.id);
    },
    isQuestionToday(question) {
      if (!question || !question.startDateTime || !question.endDateTime) {
        return false;
      }
      
      const now = new Date();
      const startDate = new Date(question.startDateTime);
      const endDate = new Date(question.endDateTime);
      
      return now >= startDate && now <= endDate;
    },
    handleCardClick() {
      // If promotional state, open rules drawer
      if (this.isQuizPromotional) {
        this.$emit('rules-click');
        return;
      }
      
      // If quiz is active
      if (this.isQuizActive) {
        // If question hasn't been answered, go to answer it
        if (!this.activeQuestionAnswered) {
          this.$emit('answer-click');
        } else {
          // If question has been answered, go to stats/dashboard
          this.$emit('show-stats');
        }
      }
      
      // If quiz is closed, always go to stats/dashboard
      if (this.isQuizClosed) {
        this.$emit('show-stats');
      }
    },
    scrollToActiveDay() {
      this.$nextTick(() => {
        const container = this.$el.querySelector('.day-indicators-small');
        if (!container) return;

        const activeDay = container.querySelector('.day-box-small.current');
        if (!activeDay) return;

        // Calculate the scroll position to center the active day
        const containerWidth = container.offsetWidth;
        const activeDayLeft = activeDay.offsetLeft;
        const activeDayWidth = activeDay.offsetWidth;
        
        // Center the active day in the container
        const scrollPosition = activeDayLeft - (containerWidth / 2) + (activeDayWidth / 2);
        
        container.scrollTo({
          left: scrollPosition,
          behavior: 'smooth'
        });
      });
    }
  },
  mounted() {
    this.scrollToActiveDay();
  },
  updated() {
    this.scrollToActiveDay();
  }
};
</script>

<style scoped>
.rightIcon {
	height: 10px;
    width: 16px;
	filter: brightness(0) invert(1);
}

.quiz-card {
  background: linear-gradient(135deg, #4a3b5c 0%, #3d2f4f 100%);
  border-radius: 8px;
  padding: 16px;
  color: white;
  position: relative;
  overflow: hidden;
  /* max-width: 335px; */
  width: 100%;
  transition: transform 0.2s ease, opacity 0.2s ease;
}

.quiz-card.clickable:active {
  transform: scale(0.98);
  opacity: 0.9;
}

/* Promotional State */
.promo-state {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* Active State */
.active-state {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* Closed State */
.closed-state {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.quiz-thumb-icon {
  width: 46.67px;
  height: 46.67px;
  min-width: 46.67px;
  border-radius: 8.33px;
  object-fit: cover;
}

.quiz-title {
  font-size: 16px !important;
  line-height: 1.125;
  color: white;
  font-weight: bold;
  margin: 0;
}

.quiz-description {
  font-size: 12px;
  line-height: 1.167;
  margin: 0;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 400;
}

.rules-link {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  cursor: pointer;
  font-weight: bold;
  font-size: 12px;
  width: 100%;
  transition: gap 0.2s ease;
}

.rules-link:hover {
  gap: 12px;
}

.day-indicators {
  display: flex;
  gap: 4px;
  flex-wrap: nowrap;
  margin-top: 8px;
  margin-left: -16px;
  margin-right: -16px;
  padding-left: 16px;
  padding-right: 16px;
  overflow-x: auto;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
}

.day-indicators::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}

.day-box {
  width: 56px !important;
  height: 56px !important;
  min-width: 56px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px !important;
  font-weight: bold;
  transition: all 0.3s ease;
  color: #4a3b5c;
}

.day-box.completed {
  background: rgba(255, 255, 255, 0.25);
  border: 2px solid rgba(255, 255, 255, 0.4);
  color: #4a3b5c;
}

/* Active State */
.active-state {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.quiz-header {
  display: flex;
  justify-Content: space-between;
  align-items: center;
  gap: 10px;
}

.quiz-info {
  flex: 1;
  min-width: 0;
}

.quiz-active-title {
  font-size: 16px !important;
  font-weight: bold;
  line-height: 1.125;
  margin: 0;
  color: white;
}

.quiz-active-description {
  font-size: 12px;
  line-height: 1.167;
  margin: 0;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 400;
}

.entries-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-left: 1px solid rgba(249, 247, 243, 0.2);
  padding-left: 16px;
  min-width: 56px;
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.entries-section:hover {
  opacity: 0.8;
}

.entries-section:active {
  opacity: 0.6;
}

.entries-label {
  font-size: 10px;
  line-height: 1.6;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 0;
}

.entries-count {
  font-size: 14px !important;
  line-height: 1.714;
  font-weight: bold;
  color: white;
}

.day-indicators-small {
  display: flex;
  gap: 4px;
  flex-wrap: nowrap;
  margin-top: 8px;
  margin-left: -16px;
  margin-right: -16px;
  padding-left: 16px;
  padding-right: 16px;
  overflow-x: auto;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
}

.day-indicators-small::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}

.day-box-small {
  width: 56px;
  height: 56px;
  min-width: 56px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: bold;
  position: relative;
  transition: all 0.3s ease;
  color: #4a3b5c;
  overflow: hidden;
}

.day-box-small.completed {
  background: rgba(255, 255, 255, 0.25);
  /* border: 2px solid rgba(255, 255, 255, 0.4); */
  color: #4a3b5c;
}

.day-box-small.current {
  background: #FCE5D4;
  border: 1px solid white;
  color: white;
}

.day-box-small.current:active {
  opacity: 0.8;
}

.day-box-small.locked {
  opacity: 0.5;
  color: #4a3b5c;
}

.peel-corner {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  object-fit: cover;
  object-position: center;
}

.day-number {
  position: relative;
  z-index: 2;
}

.thumb-icon {
  width: 110%;
  height: 110%;
  object-fit: contain;
  position: relative;
  z-index: 2;
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .quiz-card {
    padding: 16px;
  }

  .quiz-title {
    font-size: 16px;
  }

  .quiz-active-title {
    font-size: 16px;
  }

  .quiz-active-description {
    font-size: 12px;
  }

  .entries-section {
    min-width: 56px;
    padding-left: 16px;
  }

  .entries-label {
    font-size: 10px;
  }

  .entries-count {
    font-size: 14px;
  }

  .day-box {
    width: 56px;
    height: 56px;
    font-size: 18px;
  }

  .day-box-small {
    width: 56px;
    height: 56px;
    font-size: 16px;
  }
}
</style>
